/**
 * Wizard de creación de proyecto — tipos y reducer.
 *
 * El wizard captura intent en 5 pasos. El submit final llama:
 *   1. POST /api/v1/projects (campos canónicos + el resto en `metadata`).
 *   2. POST /api/v1/projects/{id}/members una vez por cada stakeholder.
 *   3. POST /api/v1/projects/{id}/members con scope=internal para el
 *      usuario actual con el rol seleccionado.
 *
 * Lo que el backend aún no acepta nativamente (lifecycle_template_key,
 * cadence, phases contract_mode, approvals_template) viaja dentro de
 * `metadata` para preservar la intención. Cuando `project_service`
 * evolucione (B.5.e.3 / B.6) podrá leerla y materializar las phases
 * reales desde el lifecycle template.
 */

import type {
  ContentLanguage,
  ProjectMethodology,
} from '../../../api/projects'
import type { RaciRole } from '../../../api/project_members'

// ─── Lifecycle templates (AI-1.f.bug: dinámico, antes hardcoded) ───────────
/** Una opción de lifecycle template visible en Step 1. Se construye en
 * runtime a partir de `GET /templates?template_type=lifecycle&template_status=published`
 * + el snapshot del current_version (para sacar nombre/descripción
 * localizados y la lista de phases que se renderiza en Step 3). */
export interface LifecycleTemplateOption {
  /** UUID del template — se manda al backend como `lifecycle_template_id`. */
  id: string
  /** `template_key` legible (ej. `tpl-lifecycle-hsl`). */
  templateKey: string
  /** Nombre canónico de la fila `templates` (no localizado). */
  name: string
  /** Descripción canónica (puede ser null). */
  description: string | null
  /** Versión actual (`X.Y.Z`). */
  version: string
  /** True si es plantilla del sistema; false si es del tenant. */
  isSystemDefault: boolean
  /** Nombre localizado de su metodología (todo ciclo pertenece a una). */
  methodologyName: string
  /** Su metodología es la predeterminada efectiva: la de la organización
   * o, si no tiene, la del producto. Se preselecciona. */
  isPreferred: boolean
}

/** Phase declarada por un lifecycle template — se materializa al
 * fetchear la `definition.phases` del current_version del lifecycle
 * activo. Step 3 la usa para renderizar dinámicamente.
 *
 * `available=false` corresponde a phases que todavía no se soportan
 * en MVP (queda en `skipped` y bloqueada). Hoy todas las phases reales
 * se marcan `available=true` — el flag queda como hook para futuro. */
export interface PhaseDef {
  /** `phase-0`, `phase-1`, … */
  code: string
  /** Nombre legible de la phase (i18n localizado del lifecycle). */
  name: string
  /** Display order, viene de `phase_order` del lifecycle. */
  order: number
  /** Cantidad de sub-fases declaradas. */
  subphaseCount: number
  /** Cantidad de expected_artifacts declarados — hint en UI. */
  expectedArtifactCount: number
  /** Si false, sólo permite `skipped` y bloquea selector. */
  available: boolean
  /** Default contract_mode (lo decide el lifecycle vía
   * `default_contract_mode`; fallback `internal`). */
  defaultMode: ContractModeChoice
  /** Si false, el usuario NO puede cambiar el contract_mode
   * (`contract_mode_overridable=false` en el lifecycle). */
  overridable: boolean
}

// OT-0007 — contract_mode es binario: se contrata la fase o no. Quién
// produce cada documento ('template' | 'upload') es una decisión por
// artifact en Lifecycle (`Artifact.source_mode`), no por fase — ya no
// se elige en este wizard. 'external_canon' deja de ser una opción
// nueva (el backend lo sigue tolerando en datos legacy, no en escritura).
export type ContractModeChoice = 'internal' | 'skipped'

/** Convierte la `definition.phases[]` del current_version del lifecycle
 * activo + la tabla i18n localizada en una lista de `PhaseDef` lista
 * para Step 3.
 *
 * Tolerancia: cualquier campo faltante se rellena con un default
 * sensato (mode `internal`, overridable=true, available=true). Si la
 * `definition` no tiene phases, devuelve `[]` y Step 3 muestra empty
 * state. */
export function buildPhaseDefsFromDefinition(
  definition: Record<string, unknown> | null | undefined,
  locale: 'es' | 'en',
): PhaseDef[] {
  if (!definition) return []
  const phases = definition['phases']
  if (!Array.isArray(phases)) return []
  // i18n.{locale}.phases — array paralelo (mismos phase_code) con name + description.
  const i18nBlock =
    (definition['i18n'] as Record<string, unknown> | undefined)?.[locale]
  const i18nPhases =
    i18nBlock && typeof i18nBlock === 'object' && 'phases' in i18nBlock
      ? ((i18nBlock as { phases?: unknown[] }).phases ?? [])
      : []
  const i18nByCode = new Map<string, { name?: string }>()
  for (const ph of i18nPhases as Array<Record<string, unknown>>) {
    const code = ph['phase_code']
    if (typeof code === 'string') {
      const name = ph['name']
      i18nByCode.set(code, typeof name === 'string' ? { name } : {})
    }
  }

  return (phases as Array<Record<string, unknown>>)
    .map((p, idx): PhaseDef => {
      const code = typeof p['phase_code'] === 'string' ? p['phase_code'] : `phase-${idx}`
      const order = typeof p['phase_order'] === 'number' ? p['phase_order'] : idx
      const subphases = Array.isArray(p['subphases']) ? p['subphases'] : []
      const expected = Array.isArray(p['expected_artifacts'])
        ? p['expected_artifacts']
        : []
      // OT-0007: 'external_canon' ya no es una opción del wizard. Un
      // lifecycle legacy que todavía lo declare cae a 'internal' —
      // mismo fallback tolerante que aplica el backend (Docs OT-0007
      // §4.2.3) mientras se termina de limpiar ese valor del catálogo.
      const defaultMode: ContractModeChoice =
        p['default_contract_mode'] === 'skipped' ? 'skipped' : 'internal'
      const overridable = p['contract_mode_overridable'] !== false
      const i18n = i18nByCode.get(code)
      return {
        code,
        name: i18n?.name ?? code,
        order,
        subphaseCount: subphases.length,
        expectedArtifactCount: expected.length,
        available: true,
        defaultMode,
        overridable,
      }
    })
    .sort((a, b) => a.order - b.order)
}

// ─── Cadencia (step 2) ──────────────────────────────────────────────────────
export type SprintLengthWeeks = 1 | 2 | 3 | 4
export type SprintStartDay = 'monday' | 'tuesday' | 'wednesday'
export type ReleaseCadence =
  | 'each_sprint'
  | 'two_sprints'
  | 'on_demand'

// ─── Stakeholder draft (step 4) ─────────────────────────────────────────────
// Approval Workflow Fase 0 (migración 0027): los roles de stakeholder
// pasaron al catálogo de templates con scope `client_stakeholder`. Step 4
// los recibe ya acotados a la metodología del ciclo (OT-0006) y elige el
// rol inicial de cada stakeholder nuevo. `''` = sin rol: Step 4 no deja
// avanzar así.
export type StakeholderRole = string

export interface StakeholderDraft {
  /** ID local generado en el cliente para keys de React. */
  localId: string
  email: string
  role: StakeholderRole
  /** raci sugerido — el wizard no expone selector explícito, pero lo
   * inferimos por convención: sponsor=A, reviewer=R, auditor=I,
   * final_user=I. */
  raciRole: RaciRole
}

// ─── Internal roles para el current user (step 4) ───────────────────────────
// Los roles son los `template_key` del catálogo de role templates
// (templates con `template_type='role'`). Esto unifica la taxonomía con
// `lifecycle.expected_artifacts[].role_template_key`, eliminando el
// mapping legacy. El wizard hace fetch del catálogo en runtime y Step4
// defaultea al primer role internal del ciclo. NO se
// hardcodea `'rol-pm'`: los tenants renombran y clonan sus roles.
export type InternalRoleKey = string

// ─── Approvals template (step 4) ────────────────────────────────────────────
export type ApprovalsTemplateKey = 'standard' | 'custom'

// ─── Estado completo del wizard ─────────────────────────────────────────────
export interface WizardState {
  step: 1 | 2 | 3 | 4 | 5

  // Step 1
  name: string
  client: string
  /** Si vacío al submit, el wizard genera uno con `buildAutoProjectCode`. */
  code: string
  contentLanguage: ContentLanguage
  /** AI-1.f.bug: UUID del lifecycle seleccionado. Antes era una string-key
   * hardcoded (`hsl|lite|compliance`). Hoy viaja como UUID al POST. Vacío
   * mientras no se haya seleccionado uno. */
  lifecycleTemplateId: string
  /** Snapshot del template seleccionado — Step 5 lo muestra y el reducer
   * lo usa para inicializar `phases` cuando cambia el lifecycle. */
  lifecycleTemplate: LifecycleTemplateOption | null

  // Step 2
  methodology: ProjectMethodology
  sprintLength: SprintLengthWeeks
  sprintStart: SprintStartDay
  releaseCadence: ReleaseCadence

  // Step 3 — phases dinámicas según lifecycle activo
  /** Lista resuelta de phases del lifecycle activo (para render). Se
   * recalcula cuando cambia el lifecycle seleccionado. */
  phaseDefs: PhaseDef[]
  /** Selección de contract_mode por phase_code. */
  phases: Record<string, ContractModeChoice>

  // Step 4
  internalRole: InternalRoleKey
  stakeholders: StakeholderDraft[]
  approvalsTemplate: ApprovalsTemplateKey

  // Submit state (no se resetea al cambiar de paso)
  submitting: boolean
  submitError: string | null
}

// ─── Initial state ──────────────────────────────────────────────────────────
export function buildInitialState(language: ContentLanguage): WizardState {
  return {
    step: 1,
    name: '',
    client: '',
    code: '',
    contentLanguage: language,
    lifecycleTemplateId: '',
    lifecycleTemplate: null,
    methodology: 'hybrid',
    sprintLength: 2,
    sprintStart: 'monday',
    releaseCadence: 'two_sprints',
    phaseDefs: [],
    phases: {},
    // El default real se asigna en Step4Team (primer rol interno del
    // ciclo) cuando el catálogo ya cargó. Vacío
    // inicial es intencional para evitar hardcodear `'rol-pm'`.
    internalRole: '',
    stakeholders: [],
    approvalsTemplate: 'standard',
    submitting: false,
    submitError: null,
  }
}

// ─── Action types ───────────────────────────────────────────────────────────
export type WizardAction =
  | { type: 'set_step'; step: WizardState['step'] }
  | {
      type: 'patch'
      patch: Partial<
        Omit<
          WizardState,
          | 'step'
          | 'phases'
          | 'phaseDefs'
          | 'lifecycleTemplate'
          | 'lifecycleTemplateId'
          | 'stakeholders'
        >
      >
    }
  /** Selección de un lifecycle. Sustituye lifecycleTemplate + Id +
   * phaseDefs + phases (defaults del nuevo lifecycle). */
  | {
      type: 'set_lifecycle'
      template: LifecycleTemplateOption
      phaseDefs: PhaseDef[]
    }
  | { type: 'set_phase_mode'; code: string; mode: ContractModeChoice }
  | { type: 'add_stakeholder'; role: StakeholderRole }
  | {
      type: 'patch_stakeholder'
      localId: string
      patch: Partial<Omit<StakeholderDraft, 'localId'>>
    }
  | { type: 'remove_stakeholder'; localId: string }
  | { type: 'submit_start' }
  | { type: 'submit_error'; message: string }

export function wizardReducer(
  state: WizardState,
  action: WizardAction,
): WizardState {
  switch (action.type) {
    case 'set_step':
      return { ...state, step: action.step, submitError: null }
    case 'patch':
      return { ...state, ...action.patch, submitError: null }
    case 'set_lifecycle': {
      // Inicializa phases con defaults del nuevo lifecycle.
      const phases: Record<string, ContractModeChoice> = {}
      for (const ph of action.phaseDefs) {
        phases[ph.code] = ph.defaultMode
      }
      // Otro ciclo es otra metodología: los roles elegidos (el propio y
      // los de stakeholders) pueden no existir en ella. Se vuelven a
      // elegir; Step 4 propone el primero de los roles del nuevo ciclo.
      const sameLifecycle = state.lifecycleTemplateId === action.template.id
      const internalRole = sameLifecycle ? state.internalRole : ''
      const stakeholders = sameLifecycle
        ? state.stakeholders
        : state.stakeholders.map((s) => ({ ...s, role: '' }))
      return {
        ...state,
        lifecycleTemplate: action.template,
        lifecycleTemplateId: action.template.id,
        phaseDefs: action.phaseDefs,
        phases,
        internalRole,
        stakeholders,
        submitError: null,
      }
    }
    case 'set_phase_mode':
      return {
        ...state,
        phases: { ...state.phases, [action.code]: action.mode },
        submitError: null,
      }
    case 'add_stakeholder': {
      const localId = `sh-${Date.now()}-${state.stakeholders.length}`
      return {
        ...state,
        stakeholders: [
          ...state.stakeholders,
          {
            localId,
            email: '',
            role: action.role,
            raciRole: inferRaciFromStakeholderRole(action.role),
          },
        ],
        submitError: null,
      }
    }
    case 'patch_stakeholder':
      return {
        ...state,
        stakeholders: state.stakeholders.map((s) =>
          s.localId === action.localId
            ? mergeStakeholder(s, action.patch)
            : s,
        ),
        submitError: null,
      }
    case 'remove_stakeholder':
      return {
        ...state,
        stakeholders: state.stakeholders.filter(
          (s) => s.localId !== action.localId,
        ),
        submitError: null,
      }
    case 'submit_start':
      return { ...state, submitting: true, submitError: null }
    case 'submit_error':
      return { ...state, submitting: false, submitError: action.message }
    default:
      return state
  }
}

function mergeStakeholder(
  stakeholder: StakeholderDraft,
  patch: Partial<Omit<StakeholderDraft, 'localId'>>,
): StakeholderDraft {
  // Si cambia role, recalculamos raciRole sugerido a menos que venga explícito.
  const next: StakeholderDraft = { ...stakeholder, ...patch }
  if (patch.role && !patch.raciRole) {
    next.raciRole = inferRaciFromStakeholderRole(patch.role)
  }
  return next
}

export function inferRaciFromStakeholderRole(role: StakeholderRole): RaciRole {
  // Defaults sugeridos por template_key. Acepta tanto los template_keys
  // del catálogo (`rol-client-sponsor`, ...) como los aliases legacy
  // pre-migración 0027 (`client_sponsor`, ...) por compatibilidad si
  // el wizard recibe state restaurado de un draft viejo.
  switch (role) {
    case 'rol-client-sponsor':
    case 'client_sponsor':
      return 'A'
    case 'rol-client-reviewer':
    case 'client_reviewer':
      return 'R'
    case 'rol-client-auditor':
    case 'client_auditor':
      return 'I'
    case 'rol-client-final-user':
    case 'client_final_user':
      return 'I'
    default:
      // Custom roles del tenant (sin convención conocida): default a 'C'.
      return 'C'
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────
/** Valida un email simple (RFC 5322 reducido — suficiente para UI).
 *
 * No usamos zod aquí para evitar dependencia transitoria — el wizard ya
 * es lo bastante grande. Si el catálogo crece, migrar a zod. */
export function isValidEmail(value: string): boolean {
  if (!value) return false
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

/** Genera código auto del proyecto cuando el usuario no escribe uno.
 * Toma las primeras 3 letras del cliente (o del nombre si no hay cliente),
 * uppercased + un sufijo basado en timestamp. Ej: `ACM-K3F2`. */
export function buildAutoProjectCode(name: string, client: string): string {
  const seed = (client || name).toUpperCase().replace(/[^A-Z0-9]/g, '')
  const head = seed.slice(0, 3) || 'PRJ'
  // Sufijo: 4 chars base36 del timestamp (suficientemente único en wizard).
  const tail = Date.now().toString(36).slice(-4).toUpperCase()
  return `${head}-${tail}`
}
